import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import type { GroupId } from '@leanin/shared/ids';
import { useMember, memberPhotoUrl } from '@/lib/use-session';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { NetworkDetailView } from '@/components/networks/NetworkDetailView';
import { groupDetailToNetworkVM } from '@/components/networks/adapters';
import type { TabId } from '@/components/networks/NetworkDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  fetchNetwork,
  fetchNetworkCircles,
  fetchNetworkEvents,
  fetchNetworkJoinRequests,
  fetchNetworkLeaders,
  fetchViewerGroupRole,
  groupKeys,
  useInfiniteGroupMembers,
} from '@/lib/queries/groups';
import { fetchFeed, feedKeys } from '@/lib/queries/feed';

const VALID_TABS: ReadonlyArray<TabId> = [
  'overview',
  'feed',
  'members',
  'circles',
  'events',
  'resources',
];

function toTabId(v: unknown): TabId {
  if (typeof v === 'string' && (VALID_TABS as ReadonlyArray<string>).includes(v))
    return v as TabId;
  return 'overview';
}

type Search = { tab?: TabId };

export const Route = createFileRoute('/_public/networks/$id/')({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    tab: toTabId(raw.tab),
  }),
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: NetworkDetailPage,
});

// Networks doesn't have a visitor/member split that requires a separate
// component — every role is handled by NetworkDetailView, which renders
// progressively from skeleton chrome (header + aside + tab bodies) to the
// fully-loaded view as each query lands. The role query is the only gate
// for leader-only fetches (`enabled: isLeader`); the rest stream in.
//
// Anonymous viewers (no session) get the same shape with `viewer` set to
// null and the feed / role / roster / join-requests queries disabled.
// The Feed and Members tabs are hidden by NetworkDetailView when viewer
// is null (member-only surfaces); Request-to-Join links to /auth/login
// with a `from=` return URL.
function NetworkDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();
  const signedIn = me !== null;

  const detailQuery = useQuery({
    queryKey: groupKeys.network.detail(id),
    queryFn: () => fetchNetwork(id),
  });
  const leadersQuery = useQuery({
    queryKey: groupKeys.network.leaders(id),
    queryFn: () => fetchNetworkLeaders(id),
  });
  const circlesQuery = useQuery({
    queryKey: groupKeys.network.circles(id, { includeArchived: true }),
    queryFn: () =>
      fetchNetworkCircles({
        networkId: id,
        pageSize: 12,
        includeArchived: true,
      }),
  });
  const eventsQuery = useQuery({
    queryKey: groupKeys.network.events(id, { window: 'upcoming', pageSize: 4 }),
    queryFn: () =>
      fetchNetworkEvents({ networkId: id, pageSize: 4, window: 'upcoming' }),
  });
  // Keyed under feedKeys so useCreateFeedPost/useReactToFeedPost/etc.
  // invalidations under feedKeys.lists() reach this query too — without
  // that, a post created via the Group composer never appears in this
  // detail page's feed tab until a hard reload. Disabled for anon — the
  // feed endpoint is member-only and the tab itself is hidden.
  const postsQuery = useQuery({
    queryKey: feedKeys.list({ scope: `group:${id}`, pageSize: 12 }),
    queryFn: () => fetchFeed({ scope: `group:${id}`, pageSize: 12 }),
    enabled: signedIn,
  });
  const roleQuery = useQuery({
    queryKey: groupKeys.viewerRole(id),
    queryFn: () => fetchViewerGroupRole(id),
    enabled: signedIn,
  });

  const rawRole = roleQuery.data;
  const role: 'leader' | 'member' | null =
    rawRole === 'leader' ? 'leader' : rawRole === 'member' ? 'member' : null;
  const isLeader = role === 'leader';

  // Roster search: the displayed value updates immediately; the debounced
  // value drives the query key so the server only gets called 300ms after
  // the user stops typing — avoids a request-per-keystroke.
  const [rosterSearchInput, setRosterSearchInput] = useState('');
  const rosterSearchDebounced = useDebouncedValue(rosterSearchInput, 300);

  const rosterQuery = useInfiniteGroupMembers(id, {
    pageSize: 24,
    enabled: signedIn,
    search: rosterSearchDebounced || undefined,
  });
  const joinRequestsQuery = useQuery({
    queryKey: groupKeys.network.joinRequests(id),
    queryFn: () => fetchNetworkJoinRequests(id),
    enabled: isLeader,
  });

  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // goes to TanStack's catch-boundary, not its 404 surface).
  if (isNotFoundApiError(detailQuery.error)) return <NotFoundPage />;
  // Other errors after the detail query has had a chance to fail — route
  // to the catch-boundary so the loading skeleton doesn't render forever.
  if (detailQuery.isError && !detailQuery.data) throw notFound();

  // Aggregate VM when the base slices (detail + leaders + circles + events)
  // are all in. Feed / roster / pending-join-requests stream independently
  // (passed alongside as their queries resolve).
  const detail = detailQuery.data ?? null;
  const leaders = leadersQuery.data?.items ?? null;
  const circles = circlesQuery.data?.items ?? null;
  const events = eventsQuery.data?.items ?? null;
  const feedItems = postsQuery.data?.items ?? null;
  const rosterItems = rosterQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const pendingJoinRequests = isLeader
    ? (joinRequestsQuery.data?.items ?? []).filter((r) => r.status === 'pending')
    : [];

  const network =
    detail !== null && leaders !== null && circles !== null && events !== null
      ? groupDetailToNetworkVM(
          detail,
          leaders,
          circles,
          events,
          role,
          {
            posts: feedItems ?? [],
            networkId: id as GroupId,
            rosterItems: rosterItems ?? [],
            pendingJoinRequests,
          },
        )
      : null;

  const viewerPhoto = me ? memberPhotoUrl(me) : null;

  return (
    <NetworkDetailView
      networkId={id}
      network={network}
      viewerRole={role}
      viewer={
        me
          ? {
              id: me.id,
              name: `${me.firstName} ${me.lastName}`.trim(),
              photoUrl: viewerPhoto ?? null,
            }
          : null
      }
      feedItems={feedItems}
      roster={network !== null ? network.roster : null}
      rosterHasMore={rosterQuery.hasNextPage}
      onRosterLoadMore={() => void rosterQuery.fetchNextPage()}
      rosterSearch={rosterSearchInput}
      onRosterSearch={setRosterSearchInput}
    />
  );
}
